import { useState } from "react";

import type {
  FeedbackFormModel,
  FeedbackStep,
  FeedbackWidgetConfig,
} from "../types";

export type FeedbackPhase = "idle" | "open" | "submitting";

interface UseFeedbackWidgetOptions {
  config: FeedbackWidgetConfig;
  onSubmit: (payload: unknown) => Promise<void>;
}

export const useFeedbackWidget = ({
  config,
  onSubmit,
}: UseFeedbackWidgetOptions) => {
  const [phase, setPhase] = useState<FeedbackPhase>(
    config.startOpen ? "open" : "idle",
  );
  const [sentiment, setSentiment] = useState("");
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [commentText, setCommentText] = useState("");
  const [error, setError] = useState("");
  const [toastPending, setToastPending] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);

  const isStepComplete = (step: FeedbackStep): boolean => {
    switch (step.type) {
      case "sentiment":
        return sentiment !== "";
      case "rating":
        return step.rows.every(row => (ratings[row.key] ?? 0) >= 1);
      case "comment":
        return true;
    }
  };

  const isStepRevealed = (index: number): boolean => {
    if (index === 0) return true;
    const step = config.steps[index];
    if (!step) return false;
    const gateIndex = step.revealAfter ?? index - 1;
    const gateStep = config.steps[gateIndex];
    return gateStep ? isStepComplete(gateStep) : false;
  };

  const visibleStepIndexes = config.steps
    .map((_, index) => index)
    .filter(index => isStepRevealed(index));

  const allStepsComplete = config.steps.every(step => isStepComplete(step));

  const isOpen = phase !== "idle";
  const isSubmitting = phase === "submitting";

  const resetForm = () => {
    setSentiment("");
    setRatings({});
    setCommentText("");
    setError("");
  };

  const open = () => {
    resetForm();
    setToastVisible(false);
    setToastPending(false);
    setPhase("open");
  };

  const close = () => {
    if (isSubmitting) return;
    resetForm();
    setPhase("idle");
  };

  const selectSentiment = (value: string) => {
    if (isSubmitting) return;
    setSentiment(value);
  };

  const setRating = (key: string, value: number) => {
    if (isSubmitting) return;
    setRatings(prev => ({ ...prev, [key]: value }));
  };

  const setComment = (value: string) => {
    if (isSubmitting) return;
    setCommentText(value);
  };

  const handleSubmit = async () => {
    if (isSubmitting || !allStepsComplete) return;
    setError("");
    setPhase("submitting");

    const model: FeedbackFormModel = {
      sentiment,
      ratings,
      comment: commentText.trim(),
    };

    try {
      await onSubmit(model);
      resetForm();
      setPhase("idle");
      setToastPending(true);
    } catch {
      setPhase("open");
      setError(config.error.submitFailed);
    }
  };

  const completeToast = () => {
    if (toastPending) {
      setToastPending(false);
      setToastVisible(true);
    }
  };

  const dismissToast = () => setToastVisible(false);

  return {
    phase,
    sentiment,
    ratings,
    commentText,
    error,
    toastPending,
    toastVisible,
    isOpen,
    isSubmitting,
    visibleStepIndexes,
    allStepsComplete,
    open,
    close,
    selectSentiment,
    setRating,
    setComment,
    handleSubmit,
    completeToast,
    dismissToast,
  };
};

export type UseFeedbackWidgetResult = ReturnType<typeof useFeedbackWidget>;
